<template>
  <!-- Rendered on every page, including the one that carries its own chat
       surface.

       It used to be hidden on the AI page, on the grounds that two surfaces over
       one thread was redundant. That is the arrangement now: the page's band is
       where a visitor reads the conversation, and the launcher is how they reach
       it from anywhere else on that page without scrolling back to the band.
       What made it safe is that there is still only one thread: every surface
       reads the composable, so the two are two drawings of one conversation
       rather than two conversations.

       Two things had to be true for that to hold, and both are handled where
       they belong rather than here. The launcher registers itself with the
       composable on open and removes itself on close, because a page can now
       have more than one visible surface and a single "is anything visible"
       flag cannot describe that. And it turns its own live region off
       (`announce`), because the band is already announcing the same turns and a
       screen reader would otherwise read each answer twice. -->
  <div ref="chrome" class="chat" :class="{ 'is-open': chromeOpen }">
    <!-- The panel is wrapped in a transition because it had an entrance and no
         exit: the keyframe ran on the way in and the node was then deleted
         outright, so closing it read as the panel being cut away rather than
         dismissed. Both directions are declared in the styles below.

         `chromeOpen` outlives `isOpen` by the length of that exit. The phone
         sheet is the `.is-open` rules, and dropping them on the click resized
         the panel to the corner card while it was still on screen. -->
    <transition name="chat-panel" @after-leave="onPanelLeft">
      <div v-if="isOpen" ref="sheet" class="chat-panel">
        <ai-chat
          ref="panel"
          v-bind="chatSurface"
          v-model:draft="chatDraft"
          variant="panel"
          :announce="!pageHasOwnSurface"
          @send="chatSend"
          @toggle-think="chatToggleThink"
          @set-think-open="chatSetThinkOpen"
        />

        <!-- The sheet's own close. It lives inside the panel so it arrives and
             leaves with the sheet. The launcher used to be restyled into this
             spot, which put the cross on screen before the panel had faded in. -->
        <button
          v-if="isPhone"
          class="chat-sheet-close"
          type="button"
          :aria-label="t('Close the assistant')"
          @click="closeChat()"
        >
          <font-awesome-icon icon="times" aria-hidden="true" />
        </button>
      </div>
    </transition>

    <!-- A plain wa.me anchor, on purpose. The document click listener in
         analytics.js is the contact: it classifies this href and sends one
         event to GA4, Meta, ChatGPT Ads and the server copy. A click handler
         here would send that event a second time. -->
    <a
      class="chat-whatsapp"
      :href="whatsappHref"
      target="_blank"
      rel="noopener"
      :aria-label="t('Message us on WhatsApp')"
      :inert="chromeOpen ? true : null"
    >
      <font-awesome-icon :icon="['fab', 'whatsapp']" aria-hidden="true" />
    </a>

    <button
      ref="toggle"
      class="chat-toggle"
      type="button"
      :aria-expanded="launcherCloses ? 'true' : 'false'"
      :aria-label="launcherCloses ? t('Close the assistant') : t('Open the assistant')"
      :inert="sheetOpen ? true : null"
      @click="toggle"
      @pointerenter="chatPrefetch"
      @focus="chatPrefetch"
    >
      <!-- The same robot mark the panel's header wears, because a visitor
           looking for the assistant is looking for that face. On a desktop the
           mark becomes a cross while the window is open. On a phone the sheet
           has its own close, and this button stays the launcher underneath it.
           `inert` is what takes it out of the tab order there: hiding it with
           `aria-hidden` while the opening click still holds focus is blocked. -->
      <span v-if="!launcherCloses" class="chat-mark" aria-hidden="true">
        <img src="/images/chat/robot.svg" alt="" />
      </span>
      <font-awesome-icon v-else icon="times" aria-hidden="true" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The launcher, fixed to the corner of the viewport.

   It used to be `position: absolute`, which meant it scrolled away with whatever
   page it was mounted on: a sitewide chat you have to scroll back up to find is
   not sitewide. The z-index is the one the widget already carried, well above
   the scroll-to-top rail (10), and Cursor.vue shares the number but is
   `pointer-events: none`, so it blends over the panel without blocking a click.

   The column is `pointer-events: none` and its children re-enable it,
   because the gap between the panel and the launcher, and the empty space beside
   the launcher in the panel's row, are part of this box and would otherwise
   swallow clicks meant for the page underneath. */
.chat {
  align-items: flex-end;
  bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  pointer-events: none;
  position: fixed;
  /* Clears the display cutout when the page is painted edge to edge. Zero on
     every device without an inset, so the launcher does not move. */
  right: calc(1.25rem + env(safe-area-inset-right, 0px));
  z-index: 1100;

  > * {
    pointer-events: auto;
  }
}

/* One measure for the panel, and it is the launcher that decides it: the panel
   is a window rather than a page, so it is narrow on a desktop and never wider
   than the phone it is on.

   The panel's entrance and exit live here for the same reason: it is this
   chrome that arrives and leaves, and the surface inside is the same one whether
   it is drawn in the page or over it, carrying its own bubbles' rise.

   Both directions are the same distance, the same duration and the same curve.
   A leave quicker than the entrance is the usual way to make a dismissal feel
   responsive, and it is wrong here: this panel is closed by a press that has
   already landed somewhere else on the page, so the eye is at the cursor rather
   than on the panel, and a hastened exit is one the visitor never sees. It
   leaves the way it arrived.

   The `chat-rise` keyframe that used to draw the entrance went with this. A node
   that animates in through a keyframe and is then deleted outright has no way to
   animate out, so the two directions are declared by one mechanism instead. */
.chat-panel {
  position: relative;
  width: min(26rem, calc(100vw - 2.5rem));
  z-index: 1;
}

/* A phone opens the panel as a sheet that fills the screen.

   The card above the launcher is the desktop window. On a phone that card
   leaves the conversation in a short box, so the panel itself takes the
   viewport. The launcher stays in the corner, under the sheet. The close is a
   separate control inside the panel, so it cannot appear ahead of the sheet
   or jump back to the corner when the sheet leaves.

   The page behind is locked only while that sheet is up. The desktop panel
   stays a window the visitor can read past, which is why the lock is not on
   every open. */
@media (max-width: 767.98px) {
  .chat.is-open .chat-panel {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    left: 0;
    min-height: 0;
    overscroll-behavior: none;
    position: fixed;
    right: 0;
    top: 0;
    /* The desktop window is 26rem. Left and right already pin this sheet to
       the screen, so that width has to be cleared or the sheet stays a card. */
    width: auto;
    /* Above the launcher. The launcher is the layer over the WhatsApp
       circle, and it comes later in the column, so a shared layer would
       put the robot on top of the sheet. */
    z-index: 2;
  }
}

/* The sheet's close, sized to the avatar tile in the header. Hidden off a
   phone, where the launcher under the window is the close. */
.chat-sheet-close {
  align-items: center;
  background: $primary;
  border: 0;
  border-radius: $r-md;
  color: $on-dark-strong;
  cursor: pointer;
  display: none;
  font-size: 0.95rem;
  height: 2.4rem;
  justify-content: center;
  position: absolute;
  right: calc(1.25rem + env(safe-area-inset-right, 0px));
  top: calc(0.9rem + env(safe-area-inset-top, 0px));
  width: 2.4rem;
  z-index: 2;

  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }
}

@media (max-width: 767.98px) {
  .chat-sheet-close {
    display: inline-flex;
  }
}

.chat-panel-enter-active,
.chat-panel-leave-active {
  transition: opacity $dur $ease, transform $dur $ease;
}

.chat-panel-enter-from,
.chat-panel-leave-to {
  opacity: 0;
  transform: translateY(0.5rem);
}

/* The same circle as the launcher under it, in the WhatsApp green, so the
   two controls in this corner are not two blue buttons. No click handler:
   the href is what the contact tracker reads.

   It is out of the column, parked one gap above the launcher, so the window
   sits on the launcher whether or not the circle is showing. While the
   window is up the circle fades and drops by the same distance, duration and
   curve the window uses, then it is hidden. The launcher is the layer above
   it, so that drop passes behind the robot. It eases back once the window
   has left. */
.chat-whatsapp {
  align-items: center;
  background: $brand-whatsapp;
  border-radius: $r-pill;
  bottom: calc(3.4rem + 0.85rem);
  box-shadow: $shadow-whatsapp;
  color: $on-dark-strong;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.65rem;
  height: 3.4rem;
  justify-content: center;
  position: absolute;
  right: 0;
  text-decoration: none;
  transition: box-shadow $dur $ease, opacity $dur $ease, transform $dur $ease, visibility $dur $ease;
  width: 3.4rem;
  z-index: 0;

  &:hover {
    box-shadow: $shadow-lift;
    color: $on-dark-strong;
    transform: translateY(-0.15rem);
  }

  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }
}

.chat.is-open .chat-whatsapp {
  opacity: 0;
  pointer-events: none;
  transform: translateY(0.5rem);
  visibility: hidden;
}

.chat-toggle {
  align-items: center;
  background: $primary;
  border: 0;
  /* A pill rather than a radius, because the control is one icon in a circle and
     the two states are the same size. */
  border-radius: $r-pill;
  box-shadow: $shadow-primary;
  color: $on-dark-strong;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 1.15rem;
  height: 3.4rem;
  justify-content: center;
  position: relative;
  transition: box-shadow $dur $ease, transform $dur $ease;
  width: 3.4rem;
  z-index: 1;

  &:hover {
    box-shadow: $shadow-lift;
    transform: translateY(-0.15rem);
  }

  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }
}

/* The mark sits on the same primary ground the panel's avatar uses, and for the
   same reason: the robot is white with a blue face plate, so it needs a coloured
   tile to read at all. Larger than the avatar's, because this one is the target
   rather than a label beside one. */
.chat-mark {
  align-items: center;
  display: inline-flex;
  height: 2.4rem;
  justify-content: center;
  width: 2.4rem;

  img {
    height: 2.4rem;
    width: 2.4rem;
  }
}
</style>

<script>
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AiChat from '@/Components/AiChat.vue';
import { useChat } from '@/composables/useChat';
import { whatsappHref } from '@/whatsapp';

FontAwesomeLibrary.add(faTimes, faWhatsapp);

/* The floating launcher and the panel it opens.

   This file is chrome, not conversation. Everything about the thread lives in
   `useChat`, which is at module scope, so an Inertia visit remounting this
   component does not reset what the visitor asked: without that, the launcher
   would open empty on every page and the same conversation would have to be
   restarted on each navigation.

   The panel is the same `AiChat.vue` the AI page renders in the flow of its own
   band. Two chromes, one surface, one thread, and the `variant` prop is the only
   thing either of them does differently. */
export default {
  components: {
    AiChat
  },

  data() {
    return {
      /* Whether the panel is showing. Local, because it is a property of this
         chrome rather than of the conversation: closing the panel does not end
         the thread, and a reply that lands after it closes is still stored and
         still there on the next open. */
      isOpen: false,

      /* Whether the sheet's chrome is still up. It stays true through the
         close animation and drops in `onPanelLeft`, so the phone layout does
         not snap back to the corner card while the panel is still leaving. */
      chromeOpen: false,

      /* Whether this viewport is a phone. The sheet's close exists only then,
         and the launcher under it is taken out of the tab order. */
      isPhone: false,

      /* A phone close wants focus back on the launcher, but the launcher is
         inert until the sheet has left. Held here and applied in onPanelLeft. */
      pendingLauncherFocus: false,

      /* The body styles and scroll position from before a phone sheet locked
         the page. Null while the page is free to scroll. */
      pageOverflow: null,

      /* The visual-viewport listener that keeps the sheet above the keyboard.
         Null while the sheet is not holding the page. */
      onSheetViewport: null
    };
  },

  computed: {
    /* Whether this page already carries a full chat surface in the flow of its
       own content. Compared by Inertia's component name rather than by URL: it
       is the page's identity, it carries no query string and no application
       base, and it survives the route's path changing.

       It no longer decides whether this chrome is drawn, because the launcher
       is on every page. What it decides is whether this chrome is the only
       surface on screen, which is what the duplicate live region turns on. */
    pageHasOwnSurface() {
      return this.$page.component === 'Services/AiSolutions';
    },

    /* The phone sheet is up, including while it is still leaving. The launcher
       sits under it and is not the close. */
    sheetOpen() {
      return this.chromeOpen && this.isPhone;
    },

    /* On a desktop the corner button is the close while the window is open.
       On a phone that job belongs to the sheet, so the corner button stays the
       launcher even during the exit. */
    launcherCloses() {
      return this.isOpen && !this.isPhone;
    },

    /* The same company number the footer, the enquiry band and the report
       open. The shared helper is what keeps the four hrefs one link. */
    whatsappHref() {
      const business = this.$page.props.business || {};

      return whatsappHref(business.phone, this.$page.props.locale, this.$page.props.referral);
    }
  },

  mounted() {
    this.phoneQuery = window.matchMedia('(max-width: 767.98px)');
    this.isPhone = this.phoneQuery.matches;
    this.onPhoneChange = () => {
      this.isPhone = this.phoneQuery.matches;

      if (!this.isOpen) {
        return;
      }

      if (this.phoneQuery.matches) {
        this.lockPageScroll();
      } else {
        this.unlockPageScroll();
      }
    };
    this.phoneQuery.addEventListener('change', this.onPhoneChange);
  },

  watch: {
    /* Both ways out of the panel are document listeners rather than handlers on
       it, and both are armed only while it is open.

       They have to be on the document rather than on the panel because they are
       about what happens away from it: Escape has to work after the visitor has
       clicked the page behind the panel and left focus there, and a press has to
       be noticed wherever on the page it lands.

       Focus is moved into the panel on open and returned to the launcher on
       close, but it is deliberately not trapped. The panel is not modal: the
       visitor can read the page behind it while an answer streams, and a key
       trap would take that away to solve a problem this surface does not have. */
    isOpen(value) {
      if (value) {
        this.chromeOpen = true;
        this.arm();
        this.lockPageScroll();
      } else {
        this.disarm();
      }
    }
  },

  beforeUnmount() {
    /* Idempotent, so this is safe whether the panel is open or not, and it is
       what stops a listener outliving the component on an Inertia visit. */
    this.pendingLauncherFocus = false;
    this.disarm();
    this.unlockPageScroll();

    if (this.phoneQuery) {
      this.phoneQuery.removeEventListener('change', this.onPhoneChange);
    }

    /* An Inertia visit can unmount this while the panel is open, and the
       composable has to stop counting this surface as visible or it would keep
       asking for chips on behalf of a panel that no longer exists. Done here
       rather than through closeChat() because there is no launcher left to put
       focus back on. */
    if (this.isOpen) {
      this.panelClose('panel');
    }
  },

  methods: {
    /* Paired with `disarm`, so the two lists cannot drift apart and a listener
       cannot be left behind. */
    arm() {
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('mousedown', this.onAway, true);
    },

    disarm() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('mousedown', this.onAway, true);
    },

    /* A phone sheet covers the page, so the page must not scroll under it.
       Hiding overflow is not enough there: the document still moves. Pinning
       the body is what holds it, and the scroll position is put back when the
       sheet has gone. Wider than a phone the panel is a window and the page
       stays free, which is why a desktop open never takes the lock. */
    lockPageScroll() {
      if (!this.phoneQuery || !this.phoneQuery.matches || this.pageOverflow !== null) {
        return;
      }

      const body = document.body;
      const scrollY = window.scrollY;

      this.pageOverflow = {
        overflow: body.style.overflow,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        scrollY
      };

      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';

      /* The panel is created in the same tick as the lock, so the sheet ref
         is not there until the DOM has flushed. */
      this.$nextTick(() => this.bindSheetViewport());
    },

    unlockPageScroll() {
      if (this.pageOverflow === null) {
        return;
      }

      const saved = this.pageOverflow;
      const body = document.body;

      this.releaseSheetViewport();
      this.pageOverflow = null;
      body.style.overflow = saved.overflow;
      body.style.position = saved.position;
      body.style.top = saved.top;
      body.style.left = saved.left;
      body.style.right = saved.right;
      body.style.width = saved.width;
      window.scrollTo(0, saved.scrollY);
    },

    /* `dvh` follows the browser chrome, not the keyboard. With the page pinned,
       the phone also stops scrolling the field up, so the sheet itself has to
       shrink to the visible viewport or the visitor types under the keyboard. */
    bindSheetViewport() {
      const viewport = window.visualViewport;

      if (!viewport || this.pageOverflow === null || this.onSheetViewport) {
        return;
      }

      this.onSheetViewport = () => this.syncSheetViewport();
      viewport.addEventListener('resize', this.onSheetViewport);
      viewport.addEventListener('scroll', this.onSheetViewport);
      this.syncSheetViewport();
    },

    syncSheetViewport() {
      const viewport = window.visualViewport;
      const sheet = this.$refs.sheet;

      if (!viewport || !sheet || this.pageOverflow === null) {
        return;
      }

      /* The address bar already moves `dvh`. Only a real keyboard, which is a
         much taller change, should pull the sheet down to the visible band.
         Applying the offset while the keyboard is closed shifts the sheet by
         the page's scroll position. */
      const keyboardOpen = window.innerHeight - viewport.height > 150;

      if (!keyboardOpen) {
        sheet.style.top = '';
        sheet.style.height = '';
        return;
      }

      sheet.style.top = `${viewport.offsetTop}px`;
      sheet.style.height = `${viewport.height}px`;
    },

    releaseSheetViewport() {
      const viewport = window.visualViewport;

      if (viewport && this.onSheetViewport) {
        viewport.removeEventListener('resize', this.onSheetViewport);
        viewport.removeEventListener('scroll', this.onSheetViewport);
      }

      this.onSheetViewport = null;

      const sheet = this.$refs.sheet;

      if (!sheet) {
        return;
      }

      sheet.style.top = '';
      sheet.style.height = '';
    },

    /* The exit has finished and the panel is gone, so the phone sheet can
       stand down with it. Doing this on the click instead resized the panel
       to the corner card for the length of the animation. */
    onPanelLeft() {
      this.chromeOpen = false;
      this.unlockPageScroll();

      if (!this.pendingLauncherFocus) {
        return;
      }

      this.pendingLauncherFocus = false;
      this.focusLauncher();
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeChat();
      }
    },

    /* A press anywhere outside this chrome closes the panel.

       The same shape as the frame's own away-detection: the capture phase, so
       the press is seen before the target's own handler, and containment is
       what keeps a press inside the panel, or on the toggle, from closing it.
       The toggle is inside the chrome on purpose, so this stands aside and lets
       the button's own click close the panel once, rather than the two racing.

       The boundary is the element, taken by ref, and not `$el`. `$el` is the
       root *node*, and this template opens with a block comment, which makes the
       root a fragment whose first node is an empty text node: `$el` was that
       text node, `Node.contains` exists on text nodes and is true only of the
       node itself, so every press read as a press on the page and the panel
       closed on a press inside it. A ref cannot be moved by a comment.

       Only the primary button counts. Closing the assistant because someone
       opened a context menu on the page would be a surprise. */
    onAway(event) {
      const chrome = this.$refs.chrome;

      if (!chrome || chrome.contains(event.target)) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      this.closeChat(false);
    },

    toggle() {
      if (this.isOpen || this.chromeOpen) {
        this.closeChat();
        return;
      }

      this.openChat();
    },

    /* Opening is where the thread is first asked for, and where the opening
       chips are generated, both of which the composable does: a visitor who
       never opens the panel never pays for either. */
    openChat() {
      const toggle = this.$refs.toggle;

      /* The opening click leaves focus here. The phone sheet then marks this
         button inert, and a focused button cannot be hidden. */
      if (toggle && document.activeElement === toggle) {
        toggle.blur();
      }

      this.isOpen = true;
      this.panelActivate('panel');

      this.$nextTick(() => {
        const panel = this.$refs.panel;

        if (panel) {
          panel.focus();
        }
      });
    },

    /* Closing, from the toggle or from Escape.

       `restoreFocus` is what separates the two ways the panel can close. Closed
       by the visitor's own hand on the toggle or by Escape, focus has to come
       back to the launcher or it falls to the document body and a keyboard
       visitor has to tab in from the top of the page again. Closed by a press on
       the page, it must not: the visitor has just chosen what they want to look
       at, and the browser has already put focus there, so pulling it to a button
       in the corner would undo the press they just made. */
    closeChat(restoreFocus = true) {
      this.isOpen = false;
      this.panelClose('panel');

      if (!restoreFocus) {
        this.pendingLauncherFocus = false;
        return;
      }

      /* On a phone the launcher is inert for the whole exit. Focusing it now
         lands on a control the browser will not keep focused. Wait until the
         sheet has left and the launcher is a normal button again. */
      if (this.sheetOpen) {
        this.pendingLauncherFocus = true;
        return;
      }

      this.focusLauncher();
    },

    focusLauncher() {
      this.$nextTick(() => {
        const toggle = this.$refs.toggle;

        if (toggle) {
          toggle.focus();
        }
      });
    }
  },

  setup() {
    const { surface, activate, close, prefetch, send, draft, toggleThink, setThinkOpen } = useChat();
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Open the assistant': 'Open the assistant',
          'Close the assistant': 'Close the assistant',
          'Message us on WhatsApp': 'Message us on WhatsApp'
        },
        'zh-MY': {
          'Open the assistant': '打开助理',
          'Close the assistant': '关闭助理',
          'Message us on WhatsApp': '通过 WhatsApp 联系我们'
        },
        'ms-MY': {
          'Open the assistant': 'Buka pembantu',
          'Close the assistant': 'Tutup pembantu',
          'Message us on WhatsApp': 'Mesej kami di WhatsApp'
        }
      }
    });

    /* The composable's `activate` and `close` are the thread's; the methods
       above are the chrome's. Renamed on the way out so the two are never
       confused for one another, and so the panel's props arrive as the single
       bindable object the AI page binds too.

       The three that are not in `chatSurface` are the ones the panel writes to
       rather than reads from: the composer it types into, and the two halves of
       the reasoning disclosure. They are bound individually because they are
       two-way, while the surface object stays a plain list of props. */
    return {
      chatSurface: surface,
      chatDraft: draft,
      chatSend: send,
      chatToggleThink: toggleThink,
      chatSetThinkOpen: setThinkOpen,
      panelActivate: activate,
      panelClose: close,
      chatPrefetch: prefetch,
      t
    };
  }
};
</script>
